import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Database,
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Repeat,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AppDialogBody,
  AppDialogContent,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import {
  createSelection,
  downloadSelectionPdf,
  getSelection,
  getSelectionLotteries,
  getSelectionProductOptions,
  MAX_SELECTION_BOLOES,
  PdfMode,
  previewSelectionPdf,
  SelectionBolao,
  SelectionGame,
  SelectionLottery,
  SelectionPayload,
  SelectionProductOptions,
  updateSelection,
} from "@/Api/gameBankSelections";
import { getSelectContests } from "@/Api/contests";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { cn } from "@/lib/utils";
import { LotteryBadge, NumberChip, pad2 } from "../GameBank/shared";
import { GamePickerSheet } from "./GamePickerSheet";
import { PDF_STANDARD_SLOW_BOLOES, PdfMenu } from "./PdfMenu";

interface BolaoState {
  key: string;
  games_count: number;
  /** null quando o banco ainda não tem jogos da loteria */
  dezenas: number | null;
  quotas: number;
  games: SelectionGame[];
  open: boolean;
}

interface PickerTarget {
  bolaoIndex: number;
  mode: "add" | "replace";
  slot: number;
}

interface ContestOption {
  id: number;
  name: string;
}

let bolaoCounter = 0;
const nextKey = () => `bolao-${Date.now()}-${bolaoCounter++}`;

const withCurrent = (values: number[], current: number | null) =>
  Array.from(new Set(current !== null ? [...values, current] : values)).sort((a, b) => a - b);

interface BolaoActions {
  toggle: (index: number) => void;
  setGamesCount: (index: number, value: string) => void;
  setDezenas: (index: number, value: string) => void;
  setQuotas: (index: number, value: string) => void;
  remove: (index: number) => void;
  removeGame: (index: number, slot: number) => void;
  pick: (index: number, mode: PickerTarget["mode"], slot: number) => void;
}

interface BolaoCardProps {
  bolao: BolaoState;
  index: number;
  readOnly: boolean;
  options: SelectionProductOptions;
  color: string | null | undefined;
  actions: BolaoActions;
}

/** Um bolão da seleção. Memorizado: com até 1500 bolões, mexer em um não redesenha os outros */
const BolaoCard = memo(function BolaoCard({ bolao, index, readOnly, options, color, actions }: BolaoCardProps) {
  const complete = bolao.games.length >= bolao.games_count;
  const progress = bolao.games_count > 0 ? (bolao.games.length / bolao.games_count) * 100 : 0;
  const slots = Array.from({ length: bolao.games_count }, (_, slot) => bolao.games[slot] ?? null);

  return (
    <Card className="[contain-intrinsic-size:auto_80px] [content-visibility:auto]">
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="flex items-center gap-2 text-left"
            onClick={() => actions.toggle(index)}
          >
            <h2 className="text-lg font-bold">Bolão {index + 1}</h2>
            {bolao.open ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {pad2(bolao.games_count)} {bolao.games_count === 1 ? "jogo" : "jogos"} de{" "}
              {bolao.dezenas !== null ? pad2(bolao.dezenas) : "--"} dezenas · {bolao.quotas} cotas
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
                complete
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
              )}
            >
              {complete && <CheckCircle2 className="h-3.5 w-3.5" />}
              {complete ? "Completo" : `${bolao.games.length} de ${bolao.games_count}`}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => actions.remove(index)}
                className="rounded p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                aria-label={`Remover bolão ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {bolao.open && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Jogos</Label>
                <Select
                  value={String(bolao.games_count)}
                  onValueChange={(value) => actions.setGamesCount(index, value)}
                  disabled={readOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {withCurrent(options.games, bolao.games_count).map((value) => (
                      <SelectItem key={value} value={String(value)}>
                        {pad2(value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dezenas</Label>
                <Select
                  value={bolao.dezenas !== null ? String(bolao.dezenas) : ""}
                  onValueChange={(value) => actions.setDezenas(index, value)}
                  disabled={readOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sem jogos no banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {withCurrent(options.dezenas, bolao.dezenas).map((value) => (
                      <SelectItem key={value} value={String(value)}>
                        {pad2(value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cotas</Label>
                <Select
                  value={String(bolao.quotas)}
                  onValueChange={(value) => actions.setQuotas(index, value)}
                  disabled={readOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {withCurrent(options.quotas, bolao.quotas).map((value) => (
                      <SelectItem key={value} value={String(value)}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Jogos define as linhas; dezenas filtra o banco; cotas é uma informação do bolão.
            </p>

            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold">Jogos do bolão</h3>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {bolao.games.length} de {bolao.games_count} selecionados
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn("h-full rounded-full", complete ? "bg-green-500" : "bg-yellow-400")}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border">
                <div className="hidden grid-cols-[5rem_6rem_1fr_9rem] gap-3 bg-muted/60 px-3 py-2 text-xs font-semibold md:grid">
                  <span>Jogo</span>
                  <span>Nº no banco</span>
                  <span>Dezenas selecionadas</span>
                  <span>Ação</span>
                </div>
                {slots.map((game, slot) => (
                  <div
                    key={slot}
                    className={cn(
                      "flex flex-col gap-2 border-t px-3 py-2.5 md:grid md:grid-cols-[5rem_6rem_1fr_9rem] md:items-center md:gap-3",
                      slot % 2 === 1 && "bg-blue-50/50 dark:bg-blue-950/10"
                    )}
                  >
                    <span className="text-sm font-medium">Jogo {slot + 1}</span>
                    <span className="text-sm">
                      {game ? String(game.game_number).padStart(3, "0") : "—"}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {game ? (
                        game.numbers.map((number, i) => (
                          <NumberChip key={`${game.id}-${i}`} value={number} color={color} />
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">Nenhum jogo escolhido</span>
                      )}
                    </div>
                    {!readOnly && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={bolao.dezenas === null}
                          onClick={() => bolao.dezenas !== null && actions.pick(index, game ? "replace" : "add", slot)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {game ? <Repeat className="h-4 w-4" /> : <Database className="h-4 w-4" />}
                          {game ? "Trocar jogo" : "Escolher jogo"}
                        </button>
                        {game && (
                          <button
                            type="button"
                            onClick={() => actions.removeGame(index, slot)}
                            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"
                            aria-label={`Remover jogo ${slot + 1}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {!readOnly && (
                  <Button
                    type="button"
                    className="border border-blue-300 bg-white text-blue-700 hover:bg-blue-50"
                    disabled={bolao.dezenas === null || complete}
                    onClick={() => actions.pick(index, "add", bolao.games.length)}
                  >
                    <Database className="mr-2 h-4 w-4" />
                    Selecionar jogos do banco
                  </Button>
                )}
                <span className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  Nenhum jogo repetido entre os bolões
                </span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
});

/**
 * Banco de Bolões > Formador de Bolões > Montar bolões (LOT-62).
 * Escolhe loteria e concurso, monta os bolões (jogos, dezenas e cotas) e puxa os jogos do Banco de Jogos.
 */
export const GameSelectionBuilderPage = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { pathname } = useLocation();
  // /formador-de-boloes/:uuid/visualizar abre a seleção só para leitura, mesmo para quem pode editar
  const viewOnly = pathname.endsWith("/visualizar");
  const isEdit = !!uuid;
  const { hasPermission, isAdmin } = usePermissions();
  const canManage = isAdmin() || hasPermission(PERMISSIONS.MANAGE_GAME_SELECTIONS);

  const [loading, setLoading] = useState(isEdit);
  const [lotteries, setLotteries] = useState<SelectionLottery[]>([]);
  const [lotteryId, setLotteryId] = useState<number | null>(null);
  const [contests, setContests] = useState<ContestOption[]>([]);
  const [contestId, setContestId] = useState<number | null>(null);
  const [options, setOptions] = useState<SelectionProductOptions>({ games: [], quotas: [], dezenas: [] });
  const [boloes, setBoloes] = useState<BolaoState[]>([]);
  const [saving, setSaving] = useState(false);

  const [picker, setPicker] = useState<PickerTarget | null>(null);

  const [pdfBusy, setPdfBusy] = useState<"preview" | "download" | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const lottery = useMemo(() => lotteries.find((item) => item.id === lotteryId) ?? null, [lotteries, lotteryId]);
  const contest = useMemo(() => contests.find((item) => item.id === contestId) ?? null, [contests, contestId]);

  const totalGames = useMemo(() => boloes.reduce((sum, bolao) => sum + bolao.games.length, 0), [boloes]);
  const incompleteCount = useMemo(
    () => boloes.filter((bolao) => bolao.games.length < bolao.games_count).length,
    [boloes]
  );
  const allSelectedIds = useMemo(() => boloes.flatMap((bolao) => bolao.games.map((game) => game.id)), [boloes]);

  const defaultBolao = useCallback(
    (opts: SelectionProductOptions, open = true): BolaoState => ({
      key: nextKey(),
      games_count: opts.games[0] ?? 1,
      dezenas: opts.dezenas[0] ?? null,
      quotas: opts.quotas[0] ?? 1,
      games: [],
      open,
    }),
    []
  );

  const loadLotteryData = useCallback(async (id: number, keepContestId?: number) => {
    const [optionsResponse, contestsResponse] = await Promise.all([
      getSelectionProductOptions(id),
      getSelectContests(id),
    ]);

    const loadedOptions: SelectionProductOptions =
      optionsResponse.ok && optionsResponse.data?.response
        ? optionsResponse.data.response
        : { games: [], quotas: [], dezenas: [] };

    setOptions(loadedOptions);

    let list: ContestOption[] =
      contestsResponse.ok && Array.isArray(contestsResponse.data?.response)
        ? contestsResponse.data.response.map((item: ContestOption) => ({ id: item.id, name: item.name }))
        : [];

    // concurso da seleção em edição pode já estar inativo e não vir na lista
    if (keepContestId && !list.some((item) => item.id === keepContestId)) {
      list = [{ id: keepContestId, name: String(keepContestId) }, ...list];
    }

    setContests(list);

    return loadedOptions;
  }, []);

  // carrega loterias e, na edição, a seleção
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const lotteriesResponse = await getSelectionLotteries();
      if (!cancelled && lotteriesResponse.ok && Array.isArray(lotteriesResponse.data?.response)) {
        setLotteries(lotteriesResponse.data.response);
      }

      if (!uuid) return;

      try {
        const { ok, data } = await getSelection(uuid);
        if (!ok || !data?.response || cancelled) return;

        const selection = data.response;
        setLotteryId(selection.lottery.id);
        setContestId(selection.contest.id);
        await loadLotteryData(selection.lottery.id, selection.contest.id);

        setContests((current) =>
          current.map((item) => (item.id === selection.contest.id ? { ...item, name: selection.contest.name } : item))
        );

        setBoloes(
          selection.boloes.map((bolao: SelectionBolao, index: number) => ({
            key: nextKey(),
            games_count: bolao.games_count,
            dezenas: bolao.dezenas,
            quotas: bolao.quotas,
            games: bolao.games,
            open: index === 0,
          }))
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uuid, loadLotteryData]);

  // libera o PDF da memória ao fechar a pré-visualização
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleLotteryChange = async (value: string) => {
    const id = Number(value);
    setLotteryId(id);
    setContestId(null);
    setContests([]);
    setBoloes([]);

    const loaded = await loadLotteryData(id);
    setBoloes([defaultBolao(loaded)]);

    if (loaded.dezenas.length === 0) {
      toast.warning("O Banco de Jogos ainda não tem jogos desta loteria. Cadastre jogos antes de montar bolões.");
    }
  };

  // os bolões atuais para as ações abaixo, que não mudam entre renderizações (o BolaoCard é memorizado)
  const boloesRef = useRef(boloes);
  useEffect(() => {
    boloesRef.current = boloes;
  }, [boloes]);

  /** Altera um bolão; os outros mantêm o mesmo objeto (e não redesenham) */
  const updateBolao = useCallback(
    (index: number, patch: Partial<BolaoState> | ((bolao: BolaoState) => Partial<BolaoState>)) =>
      setBoloes((current) =>
        current.map((bolao, i) => (i === index ? { ...bolao, ...(typeof patch === "function" ? patch(bolao) : patch) } : bolao))
      ),
    []
  );

  const bolaoActions = useMemo<BolaoActions>(
    () => ({
      toggle: (index) => updateBolao(index, (bolao) => ({ open: !bolao.open })),
      setGamesCount: (index, value) => {
        const count = Number(value);
        const bolao = boloesRef.current[index];

        if (bolao.games.length > count) {
          toast.info(`Os jogos acima de ${count} foram removidos do bolão.`);
        }

        updateBolao(index, { games_count: count, games: bolao.games.slice(0, count) });
      },
      setDezenas: (index, value) => {
        const dezenas = Number(value);
        const bolao = boloesRef.current[index];
        const hasOther = bolao.games.some((game) => game.dezenas !== dezenas);

        if (hasOther) toast.info("Os jogos escolhidos tinham outra quantidade de dezenas e foram removidos do bolão.");

        updateBolao(index, { dezenas, games: hasOther ? [] : bolao.games });
      },
      setQuotas: (index, value) => updateBolao(index, { quotas: Number(value) }),
      remove: (index) => setBoloes((current) => current.filter((_, i) => i !== index)),
      removeGame: (index, slot) =>
        updateBolao(index, (bolao) => ({ games: bolao.games.filter((_, i) => i !== slot) })),
      pick: (index, mode, slot) => setPicker({ bolaoIndex: index, mode, slot }),
    }),
    [updateBolao]
  );

  const addBolao = () => {
    if (boloes.length >= MAX_SELECTION_BOLOES) {
      toast.warning(`Máximo de ${MAX_SELECTION_BOLOES} bolões por seleção.`);
      return;
    }

    // fecha os abertos e abre o novo (só os que mudam ganham objeto novo)
    setBoloes((current) => [
      ...current.map((bolao) => (bolao.open ? { ...bolao, open: false } : bolao)),
      defaultBolao(options),
    ]);
  };

  const handlePickerConfirm = (picked: SelectionGame[]) => {
    if (!picker) return;

    const bolao = boloes[picker.bolaoIndex];

    if (picker.mode === "replace") {
      updateBolao(picker.bolaoIndex, {
        games: bolao.games.map((game, i) => (i === picker.slot ? picked[0] : game)),
      });
    } else {
      updateBolao(picker.bolaoIndex, { games: [...bolao.games, ...picked].slice(0, bolao.games_count) });
    }
  };

  const buildPayload = (): SelectionPayload | null => {
    if (!lotteryId || !contestId) {
      toast.error("Escolha a loteria e o concurso.");
      return null;
    }

    if (boloes.length === 0) {
      toast.error("Adicione ao menos um bolão.");
      return null;
    }

    if (boloes.some((bolao) => bolao.dezenas === null)) {
      toast.error("Escolha as dezenas de todos os bolões.");
      return null;
    }

    return {
      lottery_id: lotteryId,
      contest_id: contestId,
      selection_uuid: uuid,
      boloes: boloes.map((bolao) => ({
        games_count: bolao.games_count,
        dezenas: bolao.dezenas as number,
        quotas: bolao.quotas,
        game_ids: bolao.games.map((game) => game.id),
      })),
    };
  };

  const fetchPdf = async (mode: PdfMode): Promise<Blob | null> => {
    // o PDF padrão monta HTML, uma página por bolão: com muitos bolões o servidor demora (o rápido sai em segundos)
    if (mode === "standard" && boloes.length >= PDF_STANDARD_SLOW_BOLOES) {
      toast.info(`Gerando o PDF padrão de ${boloes.length} bolões. Pode demorar; o PDF rápido sai em segundos.`);
    }

    // quem só consulta gera o PDF da seleção salva; quem monta gera do que está na tela
    if ((!canManage || viewOnly) && uuid) return downloadSelectionPdf(uuid, mode);

    const payload = buildPayload();
    return payload ? previewSelectionPdf(payload, mode) : null;
  };

  const handlePreviewPdf = async (mode: PdfMode) => {
    setPdfBusy("preview");
    try {
      const blob = await fetchPdf(mode);
      if (blob) setPreviewUrl(URL.createObjectURL(blob));
    } finally {
      setPdfBusy(null);
    }
  };

  const handleDownloadPdf = async (mode: PdfMode) => {
    setPdfBusy("download");
    try {
      const blob = await fetchPdf(mode);
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `boloes-selecionados-${lottery?.name ?? "loteria"}-${contest?.name ?? "concurso"}.pdf`
        .toLowerCase()
        .replace(/\s+/g, "-");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally {
      setPdfBusy(null);
    }
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;

    setSaving(true);
    try {
      const { ok } = uuid ? await updateSelection(uuid, payload) : await createSelection(payload);

      if (ok) {
        toast.success(
          incompleteCount > 0
            ? `Seleção salva. ${incompleteCount} ${incompleteCount === 1 ? "bolão está incompleto" : "bolões estão incompletos"}.`
            : "Seleção salva com sucesso."
        );
        navigate("/formador-de-boloes");
      }
    } finally {
      setSaving(false);
    }
  };

  const pickerBolao = picker ? boloes[picker.bolaoIndex] : null;
  const readOnly = !canManage || viewOnly;
  const pdfDisabled = totalGames === 0 || pdfBusy !== null || saving;

  if (loading) {
    return (
      <Main>
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </Main>
    );
  }

  return (
    <Main>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <Link
            to="/formador-de-boloes"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para Bolões escolhidos
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {viewOnly ? "Visualizar bolões" : "Montar bolões"}
            </h1>
            {lottery && <LotteryBadge name={lottery.name} color={lottery.color} />}
          </div>
          <p className="text-sm text-muted-foreground">Concurso / Loteria e bolões</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {viewOnly && canManage && uuid && (
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => navigate(`/formador-de-boloes/${uuid}`)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {!readOnly && (
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={addBolao}
              disabled={!lotteryId || saving}
            >
              <Plus className="mr-2 h-4 w-4" />
              Adicionar bolão
            </Button>
          )}
          <PdfMenu
            className={appDialogSecondaryButton}
            label="Pré-visualizar PDF"
            icon={FileText}
            loading={pdfBusy === "preview"}
            disabled={pdfDisabled}
            boloesCount={boloes.length}
            onSelect={handlePreviewPdf}
          />
          <PdfMenu
            className="bg-orange-500 text-white hover:bg-orange-600"
            label="Gerar PDF"
            icon={Download}
            loading={pdfBusy === "download"}
            disabled={pdfDisabled}
            boloesCount={boloes.length}
            onSelect={handleDownloadPdf}
          />
        </div>
      </div>

      {/* Loteria e concurso */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
          <div className="space-y-2">
            <Label>Loteria</Label>
            <Select
              value={lotteryId ? String(lotteryId) : ""}
              onValueChange={handleLotteryChange}
              disabled={readOnly || isEdit}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a loteria" />
              </SelectTrigger>
              <SelectContent>
                {lotteries.map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Concurso</Label>
            <Select
              value={contestId ? String(contestId) : ""}
              onValueChange={(value) => setContestId(Number(value))}
              disabled={readOnly || !lotteryId}
            >
              <SelectTrigger>
                <SelectValue placeholder={lotteryId ? "Selecione o concurso" : "Escolha a loteria primeiro"} />
              </SelectTrigger>
              <SelectContent>
                {contests.map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {!lotteryId ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Escolha a loteria e o concurso para montar os bolões.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {boloes.map((bolao, index) => (
            <BolaoCard
              key={bolao.key}
              bolao={bolao}
              index={index}
              readOnly={readOnly}
              options={options}
              color={lottery?.color}
              actions={bolaoActions}
            />
          ))}
        </div>
      )}

      {/* Resumo e salvar */}
      <div className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:rounded-lg sm:border">
        <span className="text-sm text-muted-foreground">
          {boloes.length} {boloes.length === 1 ? "bolão" : "bolões"} · {totalGames}{" "}
          {totalGames === 1 ? "jogo" : "jogos"} no total
          {incompleteCount > 0 && (
            <span className="ml-2 text-yellow-700 dark:text-yellow-400">
              ({incompleteCount} {incompleteCount === 1 ? "incompleto" : "incompletos"})
            </span>
          )}
        </span>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button className={appDialogSecondaryButton} onClick={() => navigate("/formador-de-boloes")} disabled={saving}>
            {readOnly ? "Voltar" : "Cancelar"}
          </Button>
          {!readOnly && (
            <Button
              className={appDialogPrimaryButton}
              onClick={handleSave}
              disabled={saving || !lotteryId || !contestId || boloes.length === 0}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar bolões
            </Button>
          )}
        </div>
      </div>

      {/* Escolha de jogos do banco */}
      {picker && pickerBolao && pickerBolao.dezenas !== null && lotteryId && (
        <GamePickerSheet
          open
          onOpenChange={(open) => !open && setPicker(null)}
          lotteryId={lotteryId}
          dezenas={pickerBolao.dezenas}
          selectionUuid={uuid}
          excludeIds={allSelectedIds}
          maxSelect={picker.mode === "replace" ? 1 : pickerBolao.games_count - pickerBolao.games.length}
          bolaoLabel={`Bolão ${picker.bolaoIndex + 1}`}
          mode={picker.mode}
          onConfirm={handlePickerConfirm}
        />
      )}

      {/* Pré-visualização do PDF */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => !open && setPreviewUrl(null)}>
        <AppDialogContent
          icon={FileText}
          title="Pré-visualizar PDF"
          description={lottery ? `${lottery.name} · concurso ${contest?.name ?? ""}` : undefined}
          className="sm:max-w-4xl"
        >
          <AppDialogBody className="p-0 sm:p-0">
            {previewUrl && <iframe title="Pré-visualização do PDF" src={previewUrl} className="h-[75vh] w-full" />}
          </AppDialogBody>
        </AppDialogContent>
      </Dialog>

    </Main>
  );
};
